import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import { useMemo } from 'react';
import { formatDate, formatHours, resolveWorkflowButtonColor } from 'src/utils';

import { TimesheetDto } from 'src/api/FiretrolApiClient';
import { splitHoursByOvertime } from 'src/app/timesheets/splitHoursByOvertime';
import { getWorkWeekStart } from 'src/utils/workWeek';
import { LinkButton } from 'src/design/atoms/LinkButton';

import { GroupedTimesheetRow } from './utils/groupTimesheetsByUser';

type RowType = GroupedTimesheetRow | TimesheetDto;

const isGroupedRow = (row: RowType): row is GroupedTimesheetRow => {
  return 'subRows' in row && Array.isArray(row.subRows);
};

// A timesheet's own year, not "now" - rendering another timesheet's dates with the
// viewer's current year would misdate rows for prior-year timesheets.
const getWeekStart = (timesheet: TimesheetDto): moment.Moment | null => {
  if (timesheet.rows?.[0]?.startAt) {
    return moment(timesheet.rows[0].startAt);
  }
  if (timesheet.year) {
    return moment(getWorkWeekStart(timesheet.weekId, timesheet.year));
  }
  return null;
};

export const useGroupedTimesheetColumns = () => {
  const columns = useMemo<MRT_ColumnDef<RowType>[]>(
    () => [
      {
        id: 'employee',
        accessorFn: (row) =>
          isGroupedRow(row) ? row.userName : row.user?.fullName ?? '',
        header: 'Employee / Job',
        size: 300,
        Cell: ({ row }) => {
          const original = row.original;
          if (isGroupedRow(original)) {
            return (
              <Text c="black">
                {original.userName} ({original.timesheetCount} timesheet
                {original.timesheetCount !== 1 ? 's' : ''})
              </Text>
            );
          }
          const timesheet = original as TimesheetDto;
          if (timesheet.job?.jobType === 'service') {
            return (
              <Group gap="xs" wrap="nowrap">
                <LinkButton
                  size="compact-sm"
                  to={`/jobs/${timesheet.jobId}/summary`}
                >
                  Service Job
                </LinkButton>
              </Group>
            );
          }

          return (
            <Group gap="xs" wrap="nowrap">
              <LinkButton
                size="compact-sm"
                to={`/jobs/${timesheet.jobId}/summary`}
              >
                {`${timesheet?.job?.jobNumber} - ${timesheet.job?.name}`.abbreviate(
                  40,
                )}
              </LinkButton>
            </Group>
          );
        },
      },
      {
        id: 'weekId',
        accessorFn: (row) => (isGroupedRow(row) ? undefined : row.weekId),
        header: 'Week',
        sortingFn: (a, b) => {
          const aOriginal = a.original;
          const bOriginal = b.original;
          const aTimesheet = isGroupedRow(aOriginal) ? undefined : aOriginal;
          const bTimesheet = isGroupedRow(bOriginal) ? undefined : bOriginal;

          if (!aTimesheet && !bTimesheet) return 0;
          if (!aTimesheet) return 1;
          if (!bTimesheet) return -1;

          const aStart = getWeekStart(aTimesheet);
          const bStart = getWeekStart(bTimesheet);
          if (!aStart && !bStart) return 0;
          if (!aStart) return 1;
          if (!bStart) return -1;

          return aStart.diff(bStart);
        },
        Cell: ({ row }) => {
          const original = row.original;
          if (isGroupedRow(original)) {
            return null;
          }
          const timesheet = original as TimesheetDto;
          if (!timesheet.weekId) return 'N/A';
          const start = getWeekStart(timesheet);
          if (!start) return 'N/A';
          return `${formatDate(start.toDate(), 'YYYY')}, ${timesheet.weekId}`;
        },
      },
      {
        id: 'status',
        accessorFn: (row) => (isGroupedRow(row) ? undefined : row.status),
        header: 'Status',
        filterVariant: 'multi-select',
        Cell: ({ row }) => {
          const original = row.original;
          if (isGroupedRow(original)) {
            return null;
          }
          const timesheet = original as TimesheetDto;
          const step = timesheet.workflow?.steps?.[timesheet.stepIndex ?? 0];
          const color = resolveWorkflowButtonColor(step?.buttonColor) ?? 'grey';
          return <Badge color={color}>{timesheet.status}</Badge>;
        },
      },
      {
        id: 'hours',
        accessorFn: (row) =>
          isGroupedRow(row)
            ? row.regularHours
            : splitHoursByOvertime(row.rows).regularHours,
        header: 'Hours',
        filterVariant: 'range',
        Cell: ({ row }) => {
          const original = row.original;
          if (isGroupedRow(original)) {
            return (
              <Text c="black">
                {formatHours(original.regularHours, { decimals: 1 })}
              </Text>
            );
          }
          const timesheet = original as TimesheetDto;
          return formatHours(
            splitHoursByOvertime(timesheet.rows).regularHours,
            {
              decimals: 1,
            },
          );
        },
      },
      {
        id: 'overtime',
        accessorFn: (row) =>
          isGroupedRow(row)
            ? row.overtimeHours
            : splitHoursByOvertime(row.rows).overtimeHours,
        header: 'Overtime',
        filterVariant: 'range',
        Cell: ({ row }) => {
          const original = row.original;
          if (isGroupedRow(original)) {
            return (
              <Text c="black">
                {formatHours(original.overtimeHours, { decimals: 1 })}
              </Text>
            );
          }
          const timesheet = original as TimesheetDto;
          return formatHours(
            splitHoursByOvertime(timesheet.rows).overtimeHours,
            { decimals: 1 },
          );
        },
      },
      {
        id: 'payType',
        accessorFn: (row) =>
          isGroupedRow(row)
            ? undefined
            : row.job?.scalePayEnabled
            ? 'Scale'
            : 'Standard',
        header: 'Pay Type',
        Cell: ({ row }) => {
          const original = row.original;
          if (isGroupedRow(original)) {
            return null;
          }
          const timesheet = original as TimesheetDto;
          return timesheet.job?.scalePayEnabled ? 'Scale' : 'Standard';
        },
      },
    ],
    [],
  );

  return columns;
};
